import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Map, RefreshCw, Search } from "lucide-react"
import {
  smartoltService,
  type SmartOLTLiveTopology,
  type SmartOLTLiveTopologyEvent,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { SmartOLTPonTreeCanvas } from "./SmartOLTPonTreeCanvas.tsx"
import { formatUpdated, TOPOLOGY_COLORS } from "./smartoltTopologyUtils.ts"

const empty: SmartOLTLiveTopology = {
  olts: [],
  selected_olt_id: null,
  has_layout: false,
  map_markers: 0,
  reachable: false,
  online: 0,
  total: 0,
  ports: [],
  events: [],
  updated_at: null,
}

function eventTone(type: SmartOLTLiveTopologyEvent["type"]) {
  if (type === "connected") return "text-emerald-400"
  if (type === "los") return "text-rose-400"
  if (type === "power") return "text-amber-400"
  return "text-slate-400"
}

export function SmartOLTLiveTopologyPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTLiveTopology>(empty)
  const [selectedOltId, setSelectedOltId] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  const load = async (oltId?: string | null, silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getLiveTopology(oltId || undefined)
      setData({
        olts: Array.isArray(next?.olts) ? next.olts : [],
        selected_olt_id: next?.selected_olt_id || null,
        has_layout: Boolean(next?.has_layout),
        map_markers: next?.map_markers || 0,
        reachable: Boolean(next?.reachable),
        online: next?.online || 0,
        total: next?.total || 0,
        ports: Array.isArray(next?.ports) ? next.ports : [],
        events: Array.isArray(next?.events) ? next.events : [],
        updated_at: next?.updated_at || null,
      })
      setSelectedOltId(next?.selected_olt_id || null)
    } catch {
      toast.error("Failed to load live topology")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const selectedOlt = useMemo(
    () => data.olts.find((row) => row.olt_id === (selectedOltId || data.selected_olt_id)) || data.olts[0] || null,
    [data.olts, data.selected_olt_id, selectedOltId],
  )

  const showEmpty = !loading && data.total === 0
  const showPlantEmpty = !loading && !showEmpty && data.has_layout === false && data.map_markers === 0

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Live Topology</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Fiber plant from your Network Map with live ONU signals &amp; packet flow
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {data.olts.map((olt) => (
            <button key={olt.olt_id} type="button" onClick={() => load(olt.olt_id, true)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                (selectedOltId || data.selected_olt_id) === olt.olt_id
                  ? "bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-xs"
              }`}>
              {olt.name}
            </button>
          ))}
          <span className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold ${
            data.reachable
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-rose-50 text-rose-700 border border-rose-200"
          }`}>
            ● OLT {data.reachable ? "REACHABLE" : "STALE"}
          </span>
          <span className="rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
            ● ONUs {data.online}/{data.total} ONLINE
          </span>
          <Link to={SMARTOLT_PATHS.maps}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-xs hover:bg-slate-50 hover:text-blue-600 transition-colors">
            <Map className="h-3.5 w-3.5" />
            Network Map
          </Link>
        </div>
      </div>

      {!data.has_layout ? (
        <p className="text-xs text-slate-500">
          No fiber plant layout is stored for this OLT. Showing live PON hierarchy from SmartOLT sync.
          {data.map_markers ? ` ${data.map_markers} ONU GPS markers are on the Network Map.` : ""}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-slate-200 bg-slate-50/80 p-3.5 sm:p-4">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ONUs…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs" />
          </div>
          <button type="button" disabled={refreshing} onClick={() => load(selectedOltId || data.selected_olt_id, true)}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="relative h-[calc(100vh-14rem)] min-h-[520px] border-b border-slate-200 lg:border-b-0 lg:border-r lg:border-r-slate-200">
              {loading ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">Loading topology…</div>
              ) : showEmpty ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                  <Map className="h-10 w-10 text-slate-300" />
                  <div className="text-sm font-semibold text-slate-800">No ONUs found for the selected OLT</div>
                  <p className="max-w-md text-xs text-slate-500">
                    Sync configured ONUs from SmartOLT, then return here for live PON status.
                  </p>
                </div>
              ) : showPlantEmpty && data.ports.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                  <Map className="h-10 w-10 text-slate-300" />
                  <div className="text-sm font-semibold text-slate-800">No map data available for the selected OLT</div>
                  <p className="max-w-md text-xs text-slate-500">
                    Draw this OLT&apos;s fiber plant on the Network Map and the live topology will appear here.
                  </p>
                  <Link to={SMARTOLT_PATHS.maps}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-medium text-white shadow-xs transition-colors">
                    <Map className="h-3.5 w-3.5" />
                    Open Network Map
                  </Link>
                </div>
              ) : (
                <SmartOLTPonTreeCanvas
                  oltName={selectedOlt?.name || "OLT"}
                  ports={data.ports}
                  search={search}
                />
              )}

              <div className="pointer-events-none absolute bottom-3 left-3 right-3 z-10 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white/95 backdrop-blur-md px-3 py-2 text-[11px] text-slate-700 shadow-md">
                <div className="flex flex-wrap items-center gap-3 font-semibold">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full" style={{ background: TOPOLOGY_COLORS.online }} /> ONLINE
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full" style={{ background: TOPOLOGY_COLORS.fault }} /> LOS / POWER
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full" style={{ background: TOPOLOGY_COLORS.unknown }} /> NO DATA
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-slate-500">
                  <span>UPDATED {formatUpdated(data.updated_at)}</span>
                  <span>DRAG · ZOOM · CLICK ONU = PATH · 2×CLICK = OPEN</span>
                </div>
              </div>
            </div>

            <div className="max-h-[calc(100vh-18rem)] min-h-[320px] overflow-y-auto bg-slate-50/40 p-3">
              <div className="mb-3 text-sm font-semibold text-slate-800">Realtime Notifications</div>
              <div className="space-y-2">
                {data.events.length === 0 ? (
                  <p className="text-xs text-slate-500">No recent ONU status changes in the last 24 hours.</p>
                ) : (
                  data.events.map((event) => (
                    <div key={`${event.time}-${event.onu_external_id}-${event.type}`}
                      className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[11px] font-semibold ${eventTone(event.type)}`}>{event.label}</span>
                        <span className="text-[10px] text-slate-500">{formatUpdated(event.time)}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600">{event.message}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                        {event.olt_name ? (
                          selectedOlt ? (
                            <Link to={SMARTOLT_PATHS.oltDetail(selectedOlt.olt_id)} className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                              {event.olt_name}
                            </Link>
                          ) : (
                            <span className="text-slate-600">{event.olt_name}</span>
                          )
                        ) : null}
                        {event.onu_external_id ? (
                          <Link to={SMARTOLT_PATHS.onuDetail(event.onu_external_id)} className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                            {event.onu_name || event.onu_external_id}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
        </div>
      </div>
    </div>
  )
}
